import React, { useEffect, useState } from "react";

import { List } from "@mui/material";
import { Category, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { AppMenu } from "Components/AppMenu";
import { Button } from "Components/Form";
import { Filters } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { ICategory } from "Types";
import { camelCase } from "Util/camelCase";

import CategoryItem from "./components/Category";
import useStyles, { MenuTitle, OptionsMenuHeader } from "./styles";

interface FiltersMenuProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  handleClose: () => void;
  generalActions?: GeneralActions;
  handleActiveFilters?: (filters: Filters) => void;
}

export const ResourceFilterMenu = (props: FiltersMenuProps) => {
  const { anchorEl, handleClose, generalActions, handleActiveFilters } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [activeCategories, setActiveCategories] = useState<{ [x: string]: ICategory }>();

  const [menuValues, setMenu] = useState<GeneralActions["handleFilterBy"]>();
  const [filters, setFilters] = useState<Filters>();

  useEffect(() => {
    if (generalActions?.handleFilterBy?.categories) {
      setMenu(generalActions?.handleFilterBy);
      parserCategories(generalActions?.handleFilterBy?.categories);
    }
  }, [generalActions]);

  useEffect(() => {
    if (menuValues?.categories) parserCategories(menuValues?.categories);
  }, [menuValues?.categories]);

  useEffect(() => {
    if (!filters && activeCategories) {
      const updatedCategories = getObjectFilter();

      setFilters(updatedCategories);
    }
  }, [activeCategories]);

  const parserCategories = (filterCategories: Category[]) => {
    const categoriesUpdated = { ...(activeCategories || {}) };

    if (filterCategories?.length) {
      filterCategories?.forEach((category) => {
        categoriesUpdated[category?.name] = {
          ...categoriesUpdated[category?.name],
          ...category,
        };
      });
    }
    setActiveCategories(categoriesUpdated);
  };

  const addOrCreateInFilter = (category: Category, filters: Filters, value: string[]) => {
    const filtersUpdated: Filters = { ...filters };

    filtersUpdated[category?.column || camelCase(category?.name)] = value;

    return filtersUpdated;
  };

  const getObjectFilter = () => {
    let filtersUpdated: Filters = { ...filters };

    if (activeCategories) {
      const keys = Object.getOwnPropertyNames(activeCategories);

      if (keys?.length) {
        keys.map((key) => {
          const category = activeCategories[key];

          if (category?.optionsSelected && (category?.column || category?.name)) {
            filtersUpdated = addOrCreateInFilter(
              category,
              filtersUpdated,
              category?.optionsSelected
            );
          }

          return null;
        });
      }
    }

    return filtersUpdated;
  };

  const onSaveView = () => {
    handleClose();
    if (menuValues?.onChange) {
      const updatedCategories = getObjectFilter();

      if (handleActiveFilters) handleActiveFilters(updatedCategories);

      if (updatedCategories) {
        menuValues?.onChange(updatedCategories);
        setFilters(updatedCategories);
      }
    }
  };

  const onChangeCategory = (category: Category) => {
    const categoriesUpdated = { ...(activeCategories || {}) };

    categoriesUpdated[category?.name] = {
      ...categoriesUpdated[category?.name],
      ...category,
    };
    setActiveCategories(categoriesUpdated);
  };

  const renderCategories = () => {
    if (!activeCategories) return null;
    const keys = Object.getOwnPropertyNames(activeCategories);

    if (keys && keys?.length > 0) {
      return keys?.map((key) => {
        const category = activeCategories[key];

        if ((category && category?.options && category?.options?.length > 0) || category?.onSearch)
          return (
            <CategoryItem key={category?.name} category={category} onChange={onChangeCategory} />
          );

        return null;
      });
    }

    return null;
  };

  return (
    <AppMenu
      data-cy="menu-filter-by"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => handleClose()}
      disableRestoreFocus
      MenuListProps={{
        style: {
          padding: 0,
        },
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ horizontal: "center", vertical: "top" }}
    >
      <div data-cy="container">
        <OptionsMenuHeader>
          <MenuTitle>{translate("filters")}</MenuTitle>
          <Button variant="text" color="primary" onClick={onSaveView}>
            {translate("apply")}
          </Button>
        </OptionsMenuHeader>
        <List
          component="nav"
          data-cy="list-categories"
          className={classes.list}
          aria-labelledby="nested-list-subheader"
        >
          {renderCategories()}
        </List>
      </div>
    </AppMenu>
  );
};
